'use client';

import "@/styles/libs/arrow-animation.css";
import { AnimatePresence, motion } from 'framer-motion';
import React, { FC, useEffect, useState } from 'react';
import { AiFeatureCard } from './AiFeatureCard';
import { PricingModal, PricingModalProps } from './PricingModal';

const TESTIMONIAL_INTERVAL_MS = 5000;

type CustomerStoryTestimonialProps = {
  logo?: React.ReactNode;
  quote: string;
}

const TestimonialProgressBubbles = ({
  testimonials,
  currentTestimonialIndex,
  SetFixedTestimonial,
  isAnimating,
}: {
  testimonials: CustomerStoryTestimonialProps[];
  currentTestimonialIndex: number;
  SetFixedTestimonial: (index: number) => void;
  isAnimating: boolean;
}) => {

  return (
    <>
      {testimonials.map((_, index) => {
        const duration = isAnimating ? TESTIMONIAL_INTERVAL_MS / 1000 : 1;
        // isAnimating is the auto play state - animation is slower per TESTIMONIAL_INTERVAL_MS

        return isAnimating ? (
          <motion.div
            key={`progress-bubble-${index}`}
            className="bg-violet-600 relative overflow-hidden cursor-pointer mr-[4px]"
            initial={{ width: '12px', height: '1px' }}
            animate={{ width: index === currentTestimonialIndex ? ['12px', '24px', '24px', '12px'] : '12px' }}
            transition={{ duration: duration, times: [0, 0.1, 0.9, 1] }}
            onClick={() => SetFixedTestimonial(index)}
          >
            <motion.div
              className="w-[0px] h-[1px] absolute z-1  bg-violet-300 "
              initial={{ width: '0', left: '0', right: '0' }}
              animate={{
                width: index === currentTestimonialIndex ? ['0px', '24px', '0px'] : '0px',
              }}
              transition={{ duration: duration, times: [0, 0.9, 1] }}
              onClick={() => SetFixedTestimonial(index)}
            />
          </motion.div>
        ) : (
          // Not animating state - triggered by clicking on a bubble - selected bubble fills in 1s and then stays there
          <motion.div
            key={`progress-bubble-${index}`}
            className="bg-violet-600 relative overflow-hidden cursor-pointer mr-[4px]"
            initial={{ width: '12px', height: '1px' }}
            animate={{ width: index === currentTestimonialIndex ? ['12px', '24px'] : '12px' }}
            transition={{ duration: duration, times: [0, 0.9] }}
            onClick={() => SetFixedTestimonial(index)}
          >
            <motion.div
              initial={{ width: '0px', left: '0', right: '0' }}
              animate={{
                width: index === currentTestimonialIndex ? ['0px', '24px'] : '0px',
              }}
              transition={{ duration: duration, times: [0, 0.9] }}
              className="w-[12px] h-[1px] absolute z-1 bg-violet-300 "
              onClick={() => SetFixedTestimonial(index)}
            />
          </motion.div>
        );
      })}
    </>
  );
};

const DesktopCustomerStoryTestimonial = ({ testimonials }: { testimonials: CustomerStoryTestimonialProps[] }) => {
  const [currentTestimonialIndex, setCurrentTestimonialIndex] = useState(0);
  const [isAnimating, setIsAnimating] = useState(true);

  const SetFixedTestimonial = (index: number) => {
    setIsAnimating(false);
    setCurrentTestimonialIndex(index);
  };

  useEffect(() => {
    if (isAnimating) {
      const interval = setInterval(() => {
        setCurrentTestimonialIndex((prev) => (prev + 1) % testimonials.length);
      }, TESTIMONIAL_INTERVAL_MS);

      return () => clearInterval(interval);
    }
  }, [testimonials, currentTestimonialIndex, isAnimating]);

  return (
    <div className="relative h-full w-full">
      <AnimatePresence>
        {testimonials.map((testimonial, index) => {
          return (
            <motion.div
              key={`customer-story-testimonial-${index}`}
              initial={{ opacity: 0 }}
              animate={{ opacity: index === currentTestimonialIndex ? 1 : 0 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.4 }}
              className="absolute inset-0 w-full h-full  flex justify-between"
            >
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: index === currentTestimonialIndex ? 1 : 0 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.4 }}
                className="flex"
              >
                <div className="flex items-center">
                  <div className="relative flex text-blue-100 font-500 text-h4">
                    {testimonial.quote}

                  </div>
                </div>
              </motion.div>
            </motion.div>
          );
        })}
        {/* Progress bubbles */}
        <div className="absolute z-10 left-4 bottom-0 -translate-x-1/2 flex">
          <TestimonialProgressBubbles
            testimonials={testimonials}
            currentTestimonialIndex={currentTestimonialIndex}
            SetFixedTestimonial={SetFixedTestimonial}
            isAnimating={isAnimating}
          />
        </div>
      </AnimatePresence>
    </div>
  );
};

export type AiFeatureSetProps = {
  aiFeatures: {
    icon: React.ReactNode;
    title: string;
    description: string;
    priceEyebrow?: string;
    price?: string;
    priceDescription: React.ReactNode;
    priceTooltip?: boolean;
    pricingModalContent?: PricingModalProps['content'];
  }[],
  testimonials: CustomerStoryTestimonialProps[];
}

const AiFeatureSet: FC<AiFeatureSetProps> = ({
  aiFeatures = [], testimonials = [],
}) => {
  const [showPricingModal, setShowPricingModal] = useState(false);
  const [pricingModalContent, setPricingModalContent] = useState<PricingModalProps['content'] | null>(null);
  const handleTooltipClick = (content: PricingModalProps['content'] | null) => {
    setPricingModalContent(content);
    setShowPricingModal(true);
  }

  return (
    <>
      <section className="flex pt-[80px] md:pt-[120px] bg-violet-900 px-[24px] bg-no-repeat bg-[url('https://front.com/webassets/rebrand/ai-blur.svg')]" style={{ backgroundPosition: "center calc(100% - 60px)" }}>
        <div className="w-full mx-auto max-w-grid rounded-12">
          <div className="text-violet-200 text-micro3 text-center uppercase">
            Front AI Add-ons
          </div>
          <div className="text-center text-blue-100 font-500 text-h1 my-[12px]">
            Power up your plan with Front AI
          </div>
          <div className="text-center text-violet-200 text-p2 mb-[80px]">
            Meet Front AI: human-in-control AI that powers<br /> exceptional service on even your toughest tickets
          </div>
          <div className="flex flex-wrap justify-center gap-2">
            {aiFeatures.map((feature, index) => (
              <AiFeatureCard key={`ai-feature-${index}`} {...feature}
                onTooltipClick={feature.pricingModalContent ? () => handleTooltipClick(feature.pricingModalContent || null) : undefined} />
            ))}
          </div>
        </div>
      </section>
      <section className="flex pt-0 md:pt-[24px] bg-violet-900 pb-[80px] px-[24px]">
        <div className="w-full mx-auto max-w-grid flex justify-center rounded-12">
          <div className="hidden md:flex relative w-full max-w-[816px] min-h-15 rounded-[12px] p-[32px] border-solid border-[1px] border-violet-800">
            <DesktopCustomerStoryTestimonial testimonials={testimonials} />
          </div>
        </div>
      </section>
      {showPricingModal && pricingModalContent && (
        <PricingModal onClose={() => setShowPricingModal(false)} name="pricing-modal" content={pricingModalContent} />
      )}
    </>
  );
};

export default AiFeatureSet;
